import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';

const SITE_URL = 'https://vholicappliances.com';
const SITE_NAME = 'Vholic Appliances';
const DEFAULT_TITLE = 'Used Washers & Dryers in Santa Ana | Vholic Appliances';
const DEFAULT_DESCRIPTION =
  'Shop quality used washers and dryers in Santa Ana. Vholic Appliances offers affordable prices, fast delivery, installation, haul-away, and warranty coverage.';

type ProductSEORecord = {
  id: string;
  name: string;
  brand: string;
  price: number;
  description: string;
  stock_level: number;
  category: string;
  images: unknown;
  is_published: boolean;
  is_draft: boolean;
};

type StructuredData = Record<string, unknown> | Record<string, unknown>[];

type SEOConfig = {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
  nofollow?: boolean;
  type?: 'website' | 'product';
  image?: string;
  structuredData?: StructuredData;
};

function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function cleanDescription(value: string, maxLength = 160): string {
  const text = value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}

function extractImageUrls(images: unknown): string[] {
  if (!Array.isArray(images)) return [];

  return images
    .map((image) => {
      if (typeof image === 'string') return image;
      if (
        image &&
        typeof image === 'object' &&
        'url' in image &&
        typeof (image as { url?: unknown }).url === 'string'
      ) {
        return (image as { url: string }).url;
      }
      return null;
    })
    .filter((url): url is string => Boolean(url));
}

function productCondition(category: string): string {
  const normalized = category.toLowerCase();
  if (normalized === 'new') return 'https://schema.org/NewCondition';
  if (normalized === 'like new') return 'https://schema.org/RefurbishedCondition';
  return 'https://schema.org/UsedCondition';
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = href;
}

function setStructuredData(data?: StructuredData) {
  const id = 'route-seo-structured-data';
  const existing = document.getElementById(id);

  if (!data) {
    existing?.remove();
    return;
  }

  const script = existing ?? document.createElement('script');
  script.id = id;
  script.setAttribute('type', 'application/ld+json');
  script.textContent = JSON.stringify(data);

  if (!existing) document.head.appendChild(script);
}

function applySEO(config: SEOConfig) {
  const canonical = `${SITE_URL}${config.path === '/' ? '/' : config.path}`;
  const robots = config.noindex
    ? `noindex, ${config.nofollow ? 'nofollow' : 'follow'}`
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  document.title = config.title;
  setMeta('name', 'description', config.description);
  setMeta('name', 'robots', robots);
  setMeta('name', 'googlebot', robots);
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:locale', 'en_US');
  setMeta('property', 'og:type', config.type ?? 'website');
  setMeta('property', 'og:title', config.title);
  setMeta('property', 'og:description', config.description);
  setMeta('property', 'og:url', canonical);
  setMeta('name', 'twitter:card', config.image ? 'summary_large_image' : 'summary');
  setMeta('name', 'twitter:title', config.title);
  setMeta('name', 'twitter:description', config.description);

  if (config.image) {
    setMeta('property', 'og:image', config.image);
    setMeta('name', 'twitter:image', config.image);
  } else {
    document.head.querySelector('meta[property="og:image"]')?.remove();
    document.head.querySelector('meta[name="twitter:image"]')?.remove();
  }

  setCanonical(canonical);
  setStructuredData(config.structuredData);
}

function staticSEO(pathname: string): SEOConfig {
  const path = normalizePath(pathname);

  const pages: Record<string, Omit<SEOConfig, 'path'>> = {
    '/': {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION
    },
    '/shop': {
      title: 'Shop Used Washers & Dryers | Vholic Appliances',
      description:
        'Browse inspected used washers and dryers from top brands at Vholic Appliances in Santa Ana. Delivery and professional installation are available.'
    },
    '/warranty': {
      title: 'Appliance Warranty Information | Vholic Appliances',
      description:
        'Review warranty coverage for used washers and dryers purchased from Vholic Appliances in Santa Ana.'
    },
    '/faq': {
      title: 'Washer & Dryer FAQ | Vholic Appliances',
      description:
        'Find answers about used washers and dryers, delivery, installation, haul-away, payments, and warranty service from Vholic Appliances.'
    },
    '/shipping-information': {
      title: 'Delivery & Installation Information | Vholic Appliances',
      description:
        'Learn about washer and dryer delivery, professional installation, and haul-away service from Vholic Appliances in Santa Ana.'
    },
    '/cart': {
      title: `Your Cart | ${SITE_NAME}`,
      description: 'Review the appliances in your Vholic Appliances shopping cart.',
      noindex: true
    },
    '/booking': {
      title: `Book Delivery & Installation | ${SITE_NAME}`,
      description: 'Schedule appliance delivery and installation with Vholic Appliances.',
      noindex: true
    },
    '/track-order': {
      title: `Track Your Order | ${SITE_NAME}`,
      description: 'Track a Vholic Appliances delivery or installation order.',
      noindex: true
    }
  };

  if (path.startsWith('/admin')) {
    return {
      title: `Admin | ${SITE_NAME}`,
      description: 'Vholic Appliances administration area.',
      path,
      noindex: true,
      nofollow: true
    };
  }

  const page = pages[path];
  if (page) return { ...page, path };

  return {
    title: `Page Not Found | ${SITE_NAME}`,
    description: 'The requested page could not be found.',
    path,
    noindex: true
  };
}

function productSEO(product: ProductSEORecord): SEOConfig {
  const path = `/product/${encodeURIComponent(product.id)}`;
  const canonical = `${SITE_URL}${path}`;
  const images = extractImageUrls(product.images);
  const description = cleanDescription(
    product.description || `${product.name} available from Vholic Appliances in Santa Ana.`
  );
  const isIndexable = product.is_published && !product.is_draft;

  const structuredData: StructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${canonical}#product`,
      name: product.name,
      description,
      image: images.length > 0 ? images : undefined,
      sku: product.id,
      category: product.category,
      brand: {
        '@type': 'Brand',
        name: product.brand
      },
      offers: {
        '@type': 'Offer',
        url: canonical,
        priceCurrency: 'USD',
        price: product.price,
        itemCondition: productCondition(product.category),
        availability:
          product.stock_level > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: SITE_URL
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${SITE_URL}/`
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Shop',
          item: `${SITE_URL}/shop`
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: product.name,
          item: canonical
        }
      ]
    }
  ];

  return {
    title: `${product.name} | ${SITE_NAME}`,
    description,
    path,
    type: 'product',
    image: images[0],
    structuredData,
    noindex: !isIndexable
  };
}

export function SiteSEO() {
  const { pathname } = useLocation();
  const normalizedPath = normalizePath(pathname);
  const productId = normalizedPath.startsWith('/product/')
    ? decodeURIComponent(normalizedPath.replace('/product/', ''))
    : null;
  const [product, setProduct] = useState<ProductSEORecord | null>(null);
  const [productLoaded, setProductLoaded] = useState(false);

  useEffect(() => {
    let isActive = true;

    if (!productId) {
      setProduct(null);
      setProductLoaded(false);
      return () => {
        isActive = false;
      };
    }

    setProduct(null);
    setProductLoaded(false);

    supabase
      .from('products')
      .select(
        'id, name, brand, price, description, stock_level, category, images, is_published, is_draft'
      )
      .eq('id', productId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!isActive) return;
        if (error) console.error('Unable to load product SEO data:', error);
        setProduct((data as ProductSEORecord | null) ?? null);
        setProductLoaded(true);
      });

    return () => {
      isActive = false;
    };
  }, [productId]);

  const config = useMemo(() => {
    if (!productId) return staticSEO(normalizedPath);
    if (product) return productSEO(product);

    return {
      title: productLoaded ? `Product Not Found | ${SITE_NAME}` : `Product | ${SITE_NAME}`,
      description: productLoaded
        ? 'The requested appliance could not be found.'
        : 'View appliance details from Vholic Appliances.',
      path: normalizedPath,
      noindex: true
    } satisfies SEOConfig;
  }, [normalizedPath, product, productId, productLoaded]);

  useEffect(() => {
    applySEO(config);
  }, [config]);

  return null;
}
